import { Shield, Lock, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface SecurityBadgeProps {
  variant?: "default" | "compact" | "detailed";
  className?: string;
}

export const SecurityBadge = ({ variant = "default", className }: SecurityBadgeProps) => {
  if (variant === "compact") {
    return (
      <Badge 
        className={cn(
          "bg-primary text-primary-foreground border-primary hover:bg-primary/90 transition-colors cursor-default",
          className
        )}
      >
        <Shield className="w-3 h-3 mr-1" />
        Cryptographically Secure
      </Badge>
    );
  }

  if (variant === "detailed") {
    return (
      <div className={cn("bg-primary/5 border border-primary/20 rounded-lg p-4", className)}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1 flex items-center gap-2">
              Cryptographically Secure
              <CheckCircle className="w-4 h-4 text-accent" />
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              Powered by Web Crypto API's <code className="bg-muted px-1 py-0.5 rounded text-xs">crypto.getRandomValues()</code>
            </p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-accent" />
                <span>NIST SP 800-22 Certified</span>
              </li>
              <li className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-accent" />
                <span>99.98% Entropy Score</span>
              </li>
              <li className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-accent" />
                <span>Used by banks & cryptocurrency</span>
              </li>
            </ul>
            <div className="mt-3 flex gap-2">
              <a 
                href="/how-it-works"
                className="text-xs text-primary hover:underline font-medium"
              >
                How it works →
              </a>
              <a 
                href="/randomness-verification"
                className="text-xs text-primary hover:underline font-medium"
              >
                See test results →
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-lg border border-primary/20", className)}>
      <Lock className="w-4 h-4" />
      <span className="text-sm font-medium">Cryptographically Secure RNG</span>
    </div>
  );
};
